import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Wallet, RefreshCw, CheckCircle2, Clock3, XCircle, AlertTriangle } from "lucide-react";
import { fmtMoney } from "@/lib/razorpay";

function fmtDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_META = {
  paid: { label: "Paid", cls: "bg-leaf/15 text-leaf", icon: CheckCircle2 },
  created: { label: "Order created", cls: "bg-lemon/40 text-ink", icon: Clock3 },
  refunded: { label: "Refunded", cls: "bg-cloud text-stone", icon: RefreshCw },
  signature_mismatch: { label: "Failed verification", cls: "bg-watermelon-light text-watermelon", icon: AlertTriangle },
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refund = async (p) => {
    if (!window.confirm(`Refund ${fmtMoney(p.amount, p.currency)} to ${p.user_name || p.user_email}?\n\nThe student's account will be deactivated immediately.`)) return;
    setRefunding(p.id);
    try {
      await api.post(`/admin/payments/${p.id}/refund`);
      toast.success(`Refunded ${fmtMoney(p.amount, p.currency)} to ${p.user_name || "student"}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefunding(null);
    }
  };

  const totalsBy = (status) => {
    const byCur = {};
    payments.filter((p) => p.status === status).forEach((p) => {
      const c = p.currency || "INR";
      byCur[c] = (byCur[c] || 0) + (p.amount || 0);
    });
    const parts = Object.entries(byCur).map(([c, a]) => fmtMoney(a, c));
    return parts.length ? parts.join(" · ") : fmtMoney(0, "INR");
  };
  const collected = totalsBy("paid");
  const refundedTotal = totalsBy("refunded");
  const pendingCount = payments.filter((p) => p.status === "created").length;

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-payments-page">
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-leaf/15 text-leaf font-bold text-sm mb-2">
              <Wallet className="w-4 h-4" /> Payments
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Payments & refunds</h1>
            <p className="text-lg text-stone mt-1">Monthly membership fees collected via Razorpay. Refunds allowed within 3 days of payment.</p>
          </div>
          <button
            data-testid="refresh-payments"
            onClick={() => { setLoading(true); load(); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-cloud text-ink text-sm font-bold hover:border-sky"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
          <div className="p-6 bg-white rounded-3xl card-tactile" data-testid="stat-collected">
            <div className="text-xs font-bold uppercase tracking-wider text-stone mb-1">Total collected</div>
            <div className="font-heading text-3xl font-bold text-leaf">{collected}</div>
            <div className="text-xs text-stone mt-1">{payments.filter((p) => p.status === "paid").length} paid signups</div>
          </div>
          <div className="p-6 bg-white rounded-3xl card-tactile" data-testid="stat-refunded">
            <div className="text-xs font-bold uppercase tracking-wider text-stone mb-1">Refunded</div>
            <div className="font-heading text-3xl font-bold text-stone">{refundedTotal}</div>
            <div className="text-xs text-stone mt-1">{payments.filter((p) => p.status === "refunded").length} refunds issued</div>
          </div>
          <div className="p-6 bg-white rounded-3xl card-tactile" data-testid="stat-pending">
            <div className="text-xs font-bold uppercase tracking-wider text-stone mb-1">Awaiting payment</div>
            <div className="font-heading text-3xl font-bold text-watermelon">{pendingCount}</div>
            <div className="text-xs text-stone mt-1">students who haven't completed checkout</div>
          </div>
        </div>

        {loading ? (
          <div className="text-stone" data-testid="payments-loading">Loading…</div>
        ) : payments.length === 0 ? (
          <div className="p-10 bg-white rounded-3xl card-tactile text-center" data-testid="payments-empty">
            <Wallet className="w-10 h-10 text-pebble mx-auto mb-3" />
            <div className="font-heading text-lg font-bold text-ink">No payments yet</div>
            <div className="text-stone text-sm">Payments will appear here when students pay their monthly fee.</div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl card-tactile overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone border-b border-cloud">
                    <th className="px-5 py-4">Student</th>
                    <th className="px-5 py-4">Amount</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Paid at</th>
                    <th className="px-5 py-4">Order ID</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const meta = STATUS_META[p.status] || { label: p.status, cls: "bg-cloud text-stone", icon: XCircle };
                    const Icon = meta.icon;
                    return (
                      <tr key={p.id} className="border-b border-cloud/60 last:border-0" data-testid={`payment-row-${p.id}`}>
                        <td className="px-5 py-4">
                          <div className="font-bold text-ink">{p.user_name || "—"}</div>
                          <div className="text-xs text-stone">{p.user_email || ""}</div>
                        </td>
                        <td className="px-5 py-4 font-bold text-ink tabular-nums">
                          {fmtMoney(p.amount, p.currency)}
                          {p.coupon_code && (
                            <div data-testid={`payment-coupon-${p.id}`} className="text-xs font-bold text-leaf">
                              {p.coupon_code}{p.original_amount ? <span className="line-through text-stone font-normal ml-1">{fmtMoney(p.original_amount, p.currency)}</span> : null}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span data-testid={`payment-status-${p.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${meta.cls}`}>
                            <Icon className="w-3.5 h-3.5" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-stone">{fmtDate(p.paid_at)}</td>
                        <td className="px-5 py-4 text-xs text-stone font-mono">{p.order_id || p.subscription_id}</td>
                        <td className="px-5 py-4 text-right">
                          {p.status === "paid" && (
                            <button
                              data-testid={`refund-button-${p.id}`}
                              onClick={() => refund(p)}
                              disabled={refunding === p.id}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-watermelon-light text-watermelon font-bold text-xs btn-tactile hover:bg-watermelon hover:text-white disabled:opacity-60"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> {refunding === p.id ? "Refunding…" : "Refund"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
